import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Product, ProductCode } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import { criarPeca, proximoSku, type NovaPeca } from '../lib/consultas';
import { baixarCsv, paraCsv, parseCsvLinhas } from '../lib/csv';
import { paraNumero } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// Import em lote (WEB_PLAN §2.6, item 4). Serve ao mutirão de cadastro: alguém
// digita 50 linhas numa planilha em casa e sobe de uma vez.
//
// Uma linha = uma peça. As colunas de veículo criam UMA aplicação; peças que
// servem em vários carros ganham as demais aplicações pela tela de edição —
// tentar representar N aplicações por linha transformaria a planilha num
// formato que ninguém preenche certo.

const COLUNAS = [
  'sku', 'descricao', 'categoria', 'marca', 'codigo_oem', 'codigo_fabrica', 'codigo_barras',
  'posicao', 'unidade', 'ncm', 'origem_mercadoria', 'peso_kg', 'altura_cm', 'largura_cm',
  'comprimento_cm', 'preco_custo', 'preco_balcao', 'preco_ecommerce', 'quantidade', 'local',
  'estoque_min', 'estoque_max', 'reorder_point', 'montadora', 'modelo', 'ano_inicial',
  'ano_final', 'motorizacao', 'observacoes',
];

interface LinhaLida {
  numero: number;
  dados: Record<string, string>;
  erro: string | null;
}

export default function Importar() {
  const [texto, setTexto] = useState('');
  const [resultado, setResultado] = useState<{ ok: number; falhas: Array<{ linha: number; erro: string }> } | null>(null);
  const cliente = useQueryClient();

  // Arquivo que o parser recusa não vira prévia nenhuma: mostrar "1 válida"
  // para um CSV de 200 linhas que não foi lido é o pior resultado possível —
  // o gerente clica importar achando que cadastrou tudo.
  const leitura = useMemo((): { linhas: LinhaLida[]; erro: string | null } => {
    if (!texto.trim()) return { linhas: [], erro: null };
    try {
      const linhas = parseCsvLinhas(texto).map(({ numero, dados }) => ({
        numero,  // número da linha NO ARQUIVO — é por ele que o usuário procura
        dados,
        erro: validar(dados),
      }));
      return { linhas, erro: null };
    } catch (e) {
      return { linhas: [], erro: mensagemDeErro(e) };
    }
  }, [texto]);

  const linhas = leitura.linhas;
  const validas = linhas.filter((l) => !l.erro);
  const invalidas = linhas.filter((l) => l.erro);

  const importar = useMutation({
    mutationFn: async () => {
      const falhas: Array<{ linha: number; erro: string }> = [];
      let ok = 0;

      for (const linha of validas) {
        try {
          await criarPeca(await montar(linha.dados));
          ok++;
        } catch (e) {
          falhas.push({ linha: linha.numero, erro: mensagemDeErro(e) });
        }
      }
      return { ok, falhas };
    },
    onSuccess: (r) => {
      setResultado(r);
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['categorias'] });
    },
  });

  const baixarModelo = () => {
    baixarCsv('produtos-template.csv', paraCsv(COLUNAS, [
      [
        'FRE-0001', 'Pastilha de freio dianteira', 'Freios', 'Fras-le', '93286606', 'PD/1234',
        '7891234567890', 'dianteira', 'jogo', '87083090', '0', '1,2', '6', '12', '14',
        '48,00', '89,90', '95,00', '4', 'A-P3-G2', '2', '10', '3',
        'Chevrolet', 'Celta', '2000', '2015', '1.0 8V', 'Serve também no Prisma 1.0 até 2012',
      ],
    ]));
  };

  return (
    <Layout titulo="Importar CSV">
      <div className="space-y-4">
        <section className="cartao space-y-3 p-4">
          <h2 className="rotulo">Como funciona</h2>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-mute">
            <li>Baixe o modelo e preencha no Excel/Google Sheets.</li>
            <li>Só <strong className="text-paper">descricao</strong> é obrigatório — o SKU é gerado se ficar em branco.</li>
            <li>Salve como CSV, abra o arquivo aqui e confira a prévia antes de importar.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="botao-secundario" onClick={baixarModelo}>
              Baixar modelo CSV
            </button>
            <label className="botao-secundario cursor-pointer">
              Escolher arquivo
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={async (e) => {
                  const arquivo = e.target.files?.[0];
                  if (!arquivo) return;
                  setResultado(null);
                  setTexto(await arquivo.text());
                }}
              />
            </label>
          </div>
        </section>

        <section className="cartao p-4">
          <label className="rotulo" htmlFor="csv">Ou cole o conteúdo aqui</label>
          <textarea
            id="csv"
            className="campo min-h-32 font-mono text-xs"
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setResultado(null); }}
            placeholder={`${COLUNAS.slice(0, 6).join(';')};…`}
          />
        </section>

        {leitura.erro && (
          <Aviso tipo="erro">
            <p className="font-bold">O arquivo não pôde ser lido — nada foi importado.</p>
            <p className="mt-1">{leitura.erro}</p>
          </Aviso>
        )}

        {linhas.length > 0 && (
          <section className="cartao p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="rotulo">Prévia</h2>
              <p className="text-xs">
                <span className="text-ok">{validas.length} válidas</span>
                {invalidas.length > 0 && <span className="ml-2 text-erro">{invalidas.length} com erro</span>}
              </p>
            </div>

            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-dim">
                  <tr>
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Descrição</th>
                    <th className="py-2 pr-3">Marca</th>
                    <th className="py-2 pr-3">Local</th>
                    <th className="py-2 pr-3">Qtd</th>
                    <th className="py-2 pr-3">Balcão</th>
                    <th className="py-2">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {linhas.slice(0, 50).map((l) => (
                    <tr key={l.numero}>
                      <td className="py-2 pr-3 font-mono text-dim">{l.numero}</td>
                      <td className="py-2 pr-3">{l.dados.descricao || <span className="text-erro">—</span>}</td>
                      <td className="py-2 pr-3 text-mute">{l.dados.marca}</td>
                      <td className="py-2 pr-3 font-mono">{l.dados.local}</td>
                      <td className="py-2 pr-3 font-mono">{l.dados.quantidade}</td>
                      <td className="py-2 pr-3 font-mono">{l.dados.preco_balcao}</td>
                      <td className="py-2">
                        {l.erro ? <span className="text-erro">{l.erro}</span> : <span className="text-ok">ok</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {linhas.length > 50 && (
                <p className="mt-2 text-xs text-dim">
                  Mostrando as 50 primeiras de {linhas.length} linhas. Todas as válidas serão importadas.
                </p>
              )}
            </div>
          </section>
        )}

        {importar.error ? <Aviso tipo="erro">{mensagemDeErro(importar.error)}</Aviso> : null}

        {resultado && (
          <Aviso tipo={resultado.falhas.length ? 'alerta' : 'ok'}>
            <p><strong>{resultado.ok}</strong> peça(s) importada(s).</p>
            {resultado.falhas.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {resultado.falhas.slice(0, 10).map((f) => (
                  <li key={f.linha}>Linha {f.linha}: {f.erro}</li>
                ))}
              </ul>
            )}
          </Aviso>
        )}

        {validas.length > 0 && (
          <button
            type="button"
            className="botao-primario w-full py-4"
            onClick={() => importar.mutate()}
            disabled={importar.isPending}
          >
            {importar.isPending ? 'Importando…' : `Importar ${validas.length} peça(s)`}
          </button>
        )}
      </div>
    </Layout>
  );
}

function validar(d: Record<string, string>): string | null {
  if (!d.descricao?.trim()) return 'sem descrição';
  if (d.ncm?.trim() && d.ncm.replace(/\D/g, '').length !== 8) return 'NCM deve ter 8 dígitos';
  if (d.preco_balcao?.trim() && paraNumero(d.preco_balcao) === null) return 'preço inválido';
  if (d.quantidade?.trim() && paraNumero(d.quantidade) === null) return 'quantidade inválida';
  return null;
}

async function montar(d: Record<string, string>): Promise<NovaPeca> {
  const txt = (v?: string) => (v?.trim() ? v.trim() : null);
  const num = (v?: string) => (v?.trim() ? paraNumero(v) : null);

  const sku = txt(d.sku) ?? await proximoSku(d.categoria || d.marca || 'PEC');

  const produto: Partial<Product> & { sku: string; descricao: string } = {
    sku,
    descricao: d.descricao!.trim(),
    categoria: txt(d.categoria),
    marca: txt(d.marca),
    codigo_oem: txt(d.codigo_oem),
    codigo_fabrica: txt(d.codigo_fabrica),
    codigo_barras: txt(d.codigo_barras),
    posicao: txt(d.posicao),
    unidade: txt(d.unidade) ?? 'un',
    ncm: txt(d.ncm)?.replace(/\D/g, '') ?? null,
    origem_mercadoria: num(d.origem_mercadoria) ?? 0,
    peso_kg: num(d.peso_kg),
    altura_cm: num(d.altura_cm),
    largura_cm: num(d.largura_cm),
    comprimento_cm: num(d.comprimento_cm),
    preco_custo: num(d.preco_custo),
    preco_balcao: num(d.preco_balcao),
    preco_ecommerce: num(d.preco_ecommerce),
    observacoes: txt(d.observacoes),
  };

  const codigos: Array<{ tipo: ProductCode['tipo']; valor: string }> = [];
  if (produto.codigo_oem) codigos.push({ tipo: 'oem', valor: produto.codigo_oem });
  if (produto.codigo_fabrica) codigos.push({ tipo: 'fabricante', valor: produto.codigo_fabrica });
  if (produto.codigo_barras) codigos.push({ tipo: 'ean', valor: produto.codigo_barras });

  const estoque: NovaPeca['estoque'] = {};
  if (num(d.quantidade) !== null) estoque.quantidade = num(d.quantidade)!;
  if (txt(d.local)) estoque.local = txt(d.local);
  if (num(d.estoque_min) !== null) estoque.estoque_min = num(d.estoque_min)!;
  if (num(d.estoque_max) !== null) estoque.estoque_max = num(d.estoque_max)!;
  if (num(d.reorder_point) !== null) estoque.reorder_point = num(d.reorder_point)!;

  const aplicacoes = txt(d.montadora) && txt(d.modelo)
    ? [{
        montadora: d.montadora!.trim(),
        modelo: d.modelo!.trim(),
        ano_inicial: num(d.ano_inicial),
        ano_final: num(d.ano_final),
        motorizacao: txt(d.motorizacao),
        observacao: null,
      }]
    : [];

  return {
    produto,
    estoque: Object.keys(estoque).length ? estoque : undefined,
    codigos,
    aplicacoes,
  };
}
